/**
 * AssistantsContext.tsx - AI Assistants Management Context
 *
 * Provides AI assistant functionality throughout the application.
 * Manages assistant interactions including replies, posts, and text enhancement.
 * Handles language detection and AI response generation.
 * Supports fast AI endpoint with two-phase response (vector search + AI generation).
 */

import React, { createContext, useContext, ReactNode, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssistants, useProfile } from "core";
import { useCircleContext } from '@/contexts/CircleContext';
import { useChatContext, useStreamingState } from '@/contexts/ChatContext';
import { useFilterContext } from '@/contexts/FilterContext';
import { mutate } from 'swr';
import { toaster } from '@/ui/components/ui/toaster';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useChatSSE } from '@/hooks/useChatSSE';
import { usePrewarmPersonalizedQuestions } from '@/hooks/usePrewarmPersonalizedQuestions';
// import { USE_SSE_CHAT } from '@/utilities/chatConfig';
import { useApi } from "core";
const USE_SSE_CHAT = true;
interface AssistantAction {
    aindex: number;
    index: number;
    title: string;
}

interface AssistantsContextType {
    assistantList: any;
    onAssistantReply: (action: AssistantAction) => Promise<void>;
    onAssistantPost: (action: AssistantAction) => Promise<void>;
    onAssistantEnhance: (action: AssistantAction, setTitle: (title: string) => void) => Promise<void>;
    submitAssistant: { aindex: number; index: number } | null;
}

const AssistantsContext = createContext<AssistantsContextType | undefined>(undefined);

interface AssistantsProviderProps {
    children: ReactNode;
    api: any;
}

export const AssistantsProvider: React.FC<AssistantsProviderProps> = ({ children, api }) => {
    const { i18n } = useTranslation();
    const language = i18n.language;

    const { data: profile } = useProfile();
    const { parentCircleId, childCircleId, coreCircleData, parentCircleData } = useCircleContext();

    const circle = coreCircleData?.circle || parentCircleData?.circle;

    const {
        post,
        postId,
        latestResponseId,
        loading,
        setLoading,
        setFlashingBubbleId,
        setWaitingForAIResponseId,
        recentKey,
        setRecentKey
    } = useChatContext();

    const { activeFilters, hasActiveFilters } = useFilterContext();

    const {
        streamingAssistantPostId,
        setStreamingAssistantPostId,
        streamingText,
        setStreamingText,
        setStreamingVectorSearchResults,
        setStreamingComplete,
    } = useStreamingState();

    // Get circleId from URL params - this is the circle shown in the header
    const { circleId: urlCircleId } = useParams();

    // Only fetch assistants when user is authenticated
    // First, try to fetch assistants from the URL circle (the one in the header)
    const { data: urlCircleAssistantList } = useAssistants(profile ? (urlCircleId || null) : null);

    // Fall back to parent circle assistants (from ancestry)
    const parentCircleIdToUse = circle?.ancestry?.[0] || parentCircleId;
    const { data: parentAssistantList } = useAssistants(profile ? parentCircleIdToUse : null);
    
    // Use URL circle's assistants if they exist and have items, otherwise use parent's assistants
    // Priority: urlCircle with assistants > parent with assistants > urlCircle (even if empty) > parent (even if empty)
    const assistantList = (urlCircleAssistantList?.assistants && urlCircleAssistantList.assistants.length > 0) 
        ? urlCircleAssistantList 
        : (parentAssistantList?.assistants && parentAssistantList.assistants.length > 0)
        ? parentAssistantList
        : (urlCircleAssistantList || parentAssistantList);
    
    
    const postRef = useRef(post);
    const postIdRef = useRef(postId);
    const rootPostIdRef = useRef<string | null>(null);
    const rootPostDataRef = useRef<any>(null);

    const childCircleIdRef = useRef(childCircleId);
    const languageRef = useRef(language);

    const navigate = useNavigate();
    // Note: urlCircleId is already extracted from useParams above, 
    // but we need circleId for callbacks that were written before the refactor
    const circleId = urlCircleId;

    // Keep refs updated
    useEffect(() => {
        postRef.current = post;
    }, [post]);
    
    useEffect(() => {
        postIdRef.current = postId;
    }, [postId]);
    
    useEffect(() => {
        childCircleIdRef.current = childCircleId;
    }, [childCircleId]);
    
    useEffect(() => {
        languageRef.current = language;
    }, [language]);
    // Keep track of the latest action so SSE callbacks can know if this was a reply or a new chat
    const currentActionRef = useRef<{ isReply: boolean } | null>(null);

    // The follow-up chips for the NEXT chat are generated from the question just asked, so the
    // answer landing is the earliest moment they can be built — and the welcome screen that
    // needs them is the worst moment to start. Scheduled on every answer, cancelled whenever the
    // conversation continues, so a fast back-and-forth spends one generation instead of one per
    // turn. See usePrewarmPersonalizedQuestions.
    const prewarmSuggestions = usePrewarmPersonalizedQuestions(parentCircleId || null);

    // Helper to lazily load the current JWT from the shared API instance
    const coreApi = useApi();
    const getJwtToken = async (): Promise<string | undefined> => {
        try {
            const token = await coreApi.loadJwt();
            return typeof token === "string" ? token : undefined;
        } catch (error) {
            console.error("Failed to load JWT for chat SSE", error);
            return undefined;
        }
    };

    const { start: startChatSSE } = useChatSSE({
        // ============================================================
        // CALLBACK 1: Root Post Created (NEW CHATS ONLY)
        // ============================================================
        onRootPostCreated: async ({ postId: rootPostId, post: rootPost }) => {
            
            // Store the root post ID for use in other callbacks
            rootPostIdRef.current = rootPostId;
            rootPostDataRef.current = rootPost;
            
            if (!currentActionRef.current?.isReply && circleId && rootPostId) {
                const currentLanguage = languageRef.current;
                
                // STEP 1: Initialize the ROOT POST cache (the user's initial message)
                if (rootPost) {
                    // Cache key for single post (no language)
                    await mutate(
                        `/posts/${rootPostId}`,
                        rootPost,
                        { revalidate: false }
                    );
                    
                    // Cache key for post with language context
                    const postCacheKey = [`/post-view/${rootPostId}`, currentLanguage];
                    await mutate(
                        postCacheKey,
                        rootPost,
                        { revalidate: false }
                    );
                }
                
                // STEP 2: Initialize EMPTY responses cache
                // This will be populated by onUserMessageSaved callback
                const responsesCacheKey = [`/post-view-responses/${rootPostId}`, currentLanguage];
                await mutate(responsesCacheKey, {}, { revalidate: false });
                
                // console.log('✅ Cache initialized for new chat, navigating...');
                
                // STEP 3: Navigate to the new chat
                navigate(`/aichat/${circleId}/chat/${rootPostId}`);
                
                // STEP 4: Refresh the sidebar posts list
                await mutate(`/groups/${childCircleIdRef.current}/posts/_ALL/1-30`, undefined, { revalidate: true });
            }
        },
        
        // ============================================================
        // CALLBACK 2: User Message Saved
        // ============================================================
        onUserMessageSaved: async (payload) => {
            // console.log('📝 onUserMessageSaved - payload:', payload);
            
            const isReply = currentActionRef.current?.isReply;
            const currentPost = postRef.current;
            const currentChildCircleId = childCircleIdRef.current;
            const currentLanguage = languageRef.current;
            const userMessagePost = payload.post;
        
            // ============================================================
            // CASE 1: NEW CHAT - Add user message to root post's responses
            // ============================================================
            if (!isReply) {
                const rootId = rootPostIdRef.current || payload.postId;
                
                // console.log('📝 NEW chat - rootId:', rootId, 'userMessage:', userMessagePost?._id);
                
                if (userMessagePost && rootId) {
                    // Create cache key for responses
                    // Format: ['/post-view-responses/{postId}', language]
                    const responsesCacheKey = [`/post-view-responses/${rootId}`, currentLanguage];
                    
                    // Add user message to the responses cache
                    await mutate(
                        responsesCacheKey,
                        { [userMessagePost._id]: userMessagePost },
                        { revalidate: false }
                    );
                    
                    // console.log('✅ User message added to cache for new chat');
                    
                    // Trigger revalidation to sync with server
                    setTimeout(() => {
                        mutate(responsesCacheKey, undefined, { revalidate: true });
                    }, 50);
                }
            }
            
            // ============================================================
            // CASE 2: REPLY - Add user message to current post's responses
            // ============================================================
            if (isReply && currentPost?._id) {
                // console.log('📝 REPLY - currentPost:', currentPost._id, 'userMessage:', userMessagePost?._id);
                
                // Create cache key for responses of current post
                const responsesCacheKey = [`/post-view-responses/${currentPost._id}`, currentLanguage];
                
                if (userMessagePost) {
                    // Merge new message with existing responses
                    await mutate(
                        responsesCacheKey,
                        (currentData: any) => {
                            const newData = currentData ? { ...currentData } : {};
                            newData[userMessagePost._id] = userMessagePost;
                            return newData;
                        },
                        { revalidate: false }
                    );
                    
                    // console.log('✅ User message added to cache for reply');
                    
                    // Trigger revalidation to sync with server
                    setTimeout(() => {
                        mutate(responsesCacheKey, undefined, { revalidate: true });
                    }, 50);
                }
            }
        
            // Refresh circle posts in sidebar
            if (currentChildCircleId) {
                await mutate(`/groups/${currentChildCircleId}/posts/_ALL/1-30`, undefined, { revalidate: true });
            }
        },
        
        // ============================================================
        // CALLBACK 3: Vector Search Complete
        // ============================================================
        onVectorSearchComplete: (payload) => {
            // console.log('🔍 onVectorSearchComplete - results:', payload.vectorSearchResults?.length);
            
            // Store vector search results in state so ChatView can display them.
            // The post's copy first: node-api stamps the book page numbers onto that one
            // (attachSourcePageNumbers), while payload.vectorSearchResults is the raw
            // search output and would render every page of a book under one name.
            const results = payload.post?.vector_search_results?.length
                ? payload.post.vector_search_results
                : payload.vectorSearchResults;
            if (results && results.length > 0) {
                setStreamingVectorSearchResults(results);
            }
        
            const isReply = currentActionRef.current?.isReply;
            const currentLanguage = languageRef.current;
            
            // Determine which post's responses to refresh
            const rootId = isReply && postRef.current?._id 
                ? postRef.current._id 
                : rootPostIdRef.current;
            
            if (rootId) {
                const responsesCacheKey = [`/post-view-responses/${rootId}`, currentLanguage];
                mutate(responsesCacheKey, undefined, { revalidate: true });
            }
        },
        
        // ============================================================
        // CALLBACK 4: Done (Stream Complete)
        // ============================================================
        onDone: async (payload) => {
            // console.log('✅ onDone - payload:', payload);
            
            const isReply = currentActionRef.current?.isReply;
            const currentPost = postRef.current;
            const currentChildCircleId = childCircleIdRef.current;
            const currentLanguage = languageRef.current;
        
            // Determine which post to refresh
            const rootId = isReply && currentPost?._id
                ? currentPost._id
                : rootPostIdRef.current || payload.rootPostId || postIdRef.current;
        
            // Refresh responses cache to get final AI response
            if (rootId) {
                const responsesCacheKey = [`/post-view-responses/${rootId}`, currentLanguage];
                await mutate(responsesCacheKey, undefined, { revalidate: true });
            }
        
            // Refresh sidebar posts list
            if (currentChildCircleId) {
                await mutate(`/groups/${currentChildCircleId}/posts/_ALL/1-30`, undefined, { revalidate: true });
            }

            // The question behind this answer is now the newest one, which is exactly what the
            // server keys the follow-up chips on. Queue their generation so that opening a new
            // chat is a cache hit rather than a ten-second wait.
            prewarmSuggestions.schedule();
        
            // ⚠️ CRITICAL FIX: Delay clearing streaming state to prevent content disappearing
            // This gives the cache time to update before we clear the streaming text
            setTimeout(() => {
                // setStreamingAssistantPostId(null);
                // setStreamingText("");
                // setStreamingVectorSearchResults(null);
                setWaitingForAIResponseId(null);
                setLoading(false);
                
                // Clear refs
                rootPostIdRef.current = null;
                rootPostDataRef.current = null;
                
                // console.log('✅ All state cleared after completion');
            }, 500); // 500ms delay to ensure cache is updated
        },        
        
        // ============================================================
        // CALLBACK 5: Error
        // ============================================================
        onError: (payload) => {
            console.error('❌ onError:', payload);
            
            toaster.create({
                title: t("Error sending message"),
                description: payload.error || t("An unexpected error occurred"),
                type: "error",
                duration: 5000,
            });
            
            // Clear all state on error
            setStreamingAssistantPostId(null); 
            setStreamingText("");
            setStreamingVectorSearchResults(null);
            setWaitingForAIResponseId(null);
            setLoading(false);
            rootPostIdRef.current = null;
            rootPostDataRef.current = null;
        },
    });
    

    const handleAIResponse = async (action: AssistantAction, isReply: boolean) => {
        // The conversation continued, so any queued pre-warm would generate follow-ups for a
        // question that is no longer the latest. Drop it and let this answer queue its own.
        prewarmSuggestions.cancel();

        if (action.title === "") return;
    
        if (!childCircleId) {
            console.error("Cannot create post: childCircleId is not available yet");
            return;
        }
    
        if (USE_SSE_CHAT && assistantList) {
            const assistant = assistantList.assistants[action.aindex];
            const prompt = assistant.prompts[action.index];
    
            if (!assistant?._id || !prompt) {
                console.error("Missing assistant or prompt for SSE chat");
                return;
            }
    
            currentActionRef.current = { isReply };
    
            setWaitingForAIResponseId(null);
    
            // Set loading - this will show the loading indicator
            setLoading(true);
    
            const ssePostId = isReply && post?._id ? post._id : "new";
    
            try {
                const token = await getJwtToken();
                
                // ✅ FIX: Track if this is the first chunk of the new stream
                let isFirstChunkOfNewStream = true;
    
                startChatSSE(
                    {
                        postId: ssePostId,
                        message: action.title,
                        circleId: !isReply ? childCircleId : undefined,
                        assistant: assistant._id,
                        prompt: prompt.prompt,
                        token,
                        filters: hasActiveFilters() ? activeFilters : undefined,
                    },
                    {
                        onChunk: (payload) => {
                            setWaitingForAIResponseId(payload.assistantPostId);
                            setStreamingAssistantPostId(payload.assistantPostId);
                            
                            // ✅ FIX: Reset streaming text on FIRST chunk only
                            // This switches the streaming from the old post to the new post
                            if (isFirstChunkOfNewStream) {
                                setStreamingText(payload.chunk);
                                setStreamingVectorSearchResults(null);
                                setStreamingComplete(false); // Reset complete flag for new stream
                                isFirstChunkOfNewStream = false;
                            } else {
                                setStreamingText((prev) => prev + payload.chunk);
                            }
                        },
                        onComplete: (payload) => {
                            setWaitingForAIResponseId(payload.assistantPostId);
                            setStreamingAssistantPostId(payload.assistantPostId);
                            const finalContent = payload.nsHtml || payload.fullText;
                            setStreamingText(finalContent);
                            setStreamingComplete(true); // ✅ Signal that streaming is complete
                        },
                        onConnectionError: () => {
                            toaster.create({
                                title: t("Error sending message"),
                                description: t("Connection lost"),
                                type: "error",
                                duration: 5000,
                            });
                            setLoading(false);
                            // Clear streaming state on error
                            setStreamingAssistantPostId(null);
                            setStreamingText("");
                            setStreamingVectorSearchResults(null);
                        },
                    }
                );
    
                if (!isReply) {
                    setTimeout(() => {
                        setRecentKey(recentKey + 1);
                    }, 3000);
                }
            } catch (error) {
                console.error("Error starting SSE chat", error);
                toaster.create({
                    title: t("Error sending message"),
                    description: t("An unexpected error occurred"),
                    type: "error",
                    duration: 5000,
                });
                setLoading(false);
                // Clear streaming state on error
                setStreamingAssistantPostId(null);
                setStreamingText("");
                setStreamingVectorSearchResults(null);
            }
    
            return;
        }
    };

    const onAssistantReply = async (action: AssistantAction) => {
        await handleAIResponse(action, true);
    };

    const onAssistantPost = async (action: AssistantAction) => {
        await handleAIResponse(action, false);
    };

    const onAssistantEnhance = async (action: AssistantAction, setTitle: (title: string) => void) => {
        if (action.title === "") return;

        // Wait for childCircleId to be available
        if (!childCircleId) {
            console.error("Cannot enhance text: childCircleId is not available yet");
            return;
        }

        const prompt = assistantList.assistants[action.aindex].prompts[action.index];
        const assistant = assistantList.assistants[action.aindex];

        setLoading(true);

        // SSE-based enhance flow (feature flagged)
        if (USE_SSE_CHAT && assistant?._id && prompt) {
            try {
                const token = await getJwtToken();
                let enhancedText = "";

                // Use active post id when enhancing inside an existing chat; otherwise create a new chat
                const ssePostId = post?._id ? post._id : "new";

                startChatSSE(
                    {
                        postId: ssePostId,
                        message: action.title,
                        circleId: childCircleId,
                        assistant: assistant._id,
                        prompt: prompt.prompt,
                        token,
                        // Include active filters if any
                        filters: hasActiveFilters() ? activeFilters : undefined,
                    },
                    {
                        onChunk: (payload) => {
                            // Accumulate chunks for enhance mode
                            enhancedText += payload.chunk;
                        },
                        onComplete: (payload) => {
                            // Use nsHtml if available, otherwise use fullText
                            const finalContent = payload.nsHtml || payload.fullText;
                            // Strip HTML tags and decode entities
                            const cleanText = finalContent
                                .replace(/<[^>]*>/g, '')
                                .replace(/&quot;/g, '"')
                                .replace(/&#39;/g, "'")
                                .replace(/&amp;/g, '&')
                                .replace(/&lt;/g, '<')
                                .replace(/&gt;/g, '>');
                            setTitle(cleanText);
                        },
                        onDone: () => {
                            setLoading(false);
                        },
                        onError: (payload) => {
                            toaster.create({
                                title: t("Error enhancing text"),
                                description: payload.error || t("An unexpected error occurred"),
                                type: "error",
                                duration: 5000,
                            });
                            setLoading(false);
                        },
                        onConnectionError: () => {
                            toaster.create({
                                title: t("Error enhancing text"),
                                description: t("Connection lost"),
                                type: "error",
                                duration: 5000,
                            });
                            setLoading(false);
                        },
                    }
                );
            } catch (error) {
                console.error("Error starting SSE enhance", error);
                toaster.create({
                    title: t("Error enhancing text"),
                    description: t("An unexpected error occurred"),
                    type: "error",
                    duration: 5000,
                });
                setLoading(false);
            }

            return;
        }

        // Legacy /ai/add-comment-fast flow (fallback)
        try {
            const draft = await api.upsertDraft({
                attachments: [],
                tags: ["enhance-temp"],
                title: action.title,
                group: { group: childCircleId },
                type: "article",
                content: {
                    nsTitle: action.title,
                    nsHtml: action.title,
                },
            });

            const tempPost = await api.publish(draft);

            const aiResponse = await api.post({
                path: `/ai/add-comment-fast`,
                body: {
                    circleId: childCircleId,
                    prompt: prompt.prompt,
                    assistant: assistantList.assistants[action.aindex]._id,
                    content: action.title,
                    root: tempPost._id,
                    thread: tempPost._id,
                    responseTo: tempPost._id,
                }
            });

            const { post: published } = aiResponse;

            if (published && published.content && published.content.nsHtml) {
                const enhancedText = published.content.nsHtml
                    .replace(/<[^>]*>/g, '')
                    .replace('&quot;', '"')
                    .replace("&#39;", "'");
                setTitle(enhancedText);
            } else {
                console.error("Unexpected AI response structure:", aiResponse);
            }

        } catch (error: any) {
            console.error('Error enhancing text:', error);
            
            // Extract error details
            let errorTitle = t("Error enhancing text");
            let errorDescription = t("An unexpected error occurred");
            
            // Check for ApiError structure (status directly on error object)
            if (error?.status || error?.code) {
                const status = error.status || error.code;
                switch (status) {
                    case 401:
                        errorTitle = t("Authentication Error");
                        errorDescription = t("You are not authorized. Please log in again.");
                        break;
                    case 403:
                        errorTitle = t("Permission Denied");
                        errorDescription = t("You don't have permission to perform this action.");
                        break;
                    case 500:
                        errorTitle = t("Server Error");
                        errorDescription = t("The server encountered an error. Please try again later.");
                        break;
                    default:
                        errorTitle = t("Error") + ` (${status})`;
                        errorDescription = error.message || t("Failed to enhance text");
                }
            } else if (error?.message) {
                errorDescription = error.message;
            }
            
            // Show error toast
            toaster.create({
                title: errorTitle,
                description: errorDescription,
                type: "error",
                duration: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    const submitAssistant = assistantList ? assistantList.assistants.reduce((found: { aindex: number; index: number } | null, assistant: any, aindex: number) => {
        if (found) return found;
        const submitIndex = assistant.prompts.findIndex((prompt: any) => prompt.position === "aichat.submit");
        return submitIndex !== -1 ? { aindex, index: submitIndex } : null;
    }, null) : null;
    

    const contextValue: AssistantsContextType = {
        assistantList,
        onAssistantReply,
        onAssistantPost,
        onAssistantEnhance,
        submitAssistant,
    };

    return (
        <AssistantsContext.Provider value={contextValue}>
            {children}
        </AssistantsContext.Provider>
    );
};

export const useAssistantsContext = (): AssistantsContextType => {
    const context = useContext(AssistantsContext);
    if (!context) {
        throw new Error('useAssistantsContext must be used within an AssistantsProvider');
    }
    return context;
};
